import React, { useRef, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { useCalculator } from './hooks/useCalculator';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { HeroSection } from './components/HeroSection';
import { SectionWrapper } from './components/SectionWrapper';
import { SubstrateSelector } from './components/SubstrateSelector';
import { SizeSelector } from './components/SizeSelector';
import { ColorSelector } from './components/ColorSelector';
import { TextureSelector } from './components/TextureSelector';
import { SealerSelector } from './components/SealerSelector';
import { CalculatorResults } from './components/CalculatorResults';
import { ApplicationGuide } from './components/ApplicationGuide';
import { Summary } from './components/Summary';
import { ProductGallery } from './components/ProductGallery';
import { SamplePackCTA } from './components/SamplePackCTA';
import { VideoShowcase } from './components/VideoShowcase';
import { SystemArticle } from './components/SystemArticle';
import { GoldenOzPreview } from './components/GoldenOzPreview';
import { GoldenOzRoute } from './components/GoldenOzRoute';
import { Toaster } from './components/ui/sonner';
import { Card, CardContent } from './components/ui/card';
import { AlertTriangle, MessageCircle } from 'lucide-react';
import { Button } from './components/ui/button';
import { CONTACT, NATURAL_MINERALS } from './config/products';
import { PRODUCT_IMAGES } from './config/images';

const MicrolitGuide = () => {
  const { t, isRTL } = useLanguage();
  const location = useLocation();

  // If navigated here with scrollTo state, scroll to that section after mount
  useEffect(() => {
    const target = location.state?.scrollTo;
    if (target) {
      setTimeout(() => {
        const el = document.getElementById(target);
        if (el) {
          const offset = 80;
          const top = el.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: top - offset, behavior: 'smooth' });
        }
        // Clear state so refresh doesn't re-trigger
        window.history.replaceState({}, document.title);
      }, 300);
    }
  }, [location.state]);
  
  // Refs for auto-scrolling
  const sizeRef = useRef(null);
  const colorRef = useRef(null);
  const textureRef = useRef(null);
  const sealerRef = useRef(null);
  const resultsRef = useRef(null);
  const guideRef = useRef(null);
  const summaryRef = useRef(null);

  const {
    selectedSubstrate,
    selectedSize,
    customArea,
    selectedColor,
    textureType,
    includeSealer,
    area,
    substrateDetails,
    selectedPrimer,
    hasWarnings,
    calculations,
    setSelectedSubstrate,
    setSelectedSize,
    setCustomArea,
    setSelectedColor,
    setTextureType,
    setIncludeSealer,
    clearSelections,
    generateSummary,
  } = useCalculator();

  // Auto-scroll helper with smooth animation and better positioning
  const scrollToRef = (ref, offset = 80) => {
    setTimeout(() => {
      if (ref.current) {
        const elementRect = ref.current.getBoundingClientRect();
        const absoluteTop = elementRect.top + window.scrollY;
        const targetPosition = absoluteTop - offset;
        
        window.scrollTo({
          top: Math.max(0, targetPosition),
          behavior: 'smooth'
        });
      }
    }, 200);
  };

  // Quick fill for color fan menu - auto-fills with concrete, 1m², smooth, and Mika color
  const handleColorFanClick = useCallback(() => {
    // Set substrate to concrete (בטון)
    setSelectedSubstrate('concrete');
    
    // Set size to 1 m²
    const size1m = { id: 'custom', areaM2: 1, nameHe: 'מותאם אישית', nameEn: 'Custom' };
    setSelectedSize(size1m);
    setCustomArea(1);
    
    // Set texture to smooth (חלק)
    setTextureType('smooth');
    
    // Set Mika color (MM-N003)
    const mikaColor = NATURAL_MINERALS.mika;
    setSelectedColor(mikaColor);
    
    // Scroll to color section after a short delay
    setTimeout(() => {
      scrollToRef(colorRef, 80);
    }, 300);
  }, [setSelectedSubstrate, setSelectedSize, setCustomArea, setTextureType, setSelectedColor]);

  // Handlers with auto-advance
  const handleSubstrateSelect = (id) => {
    setSelectedSubstrate(id);
    scrollToRef(sizeRef, 80);
  };

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    if (size.id !== 'custom') {
      scrollToRef(colorRef, 80);
    }
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    scrollToRef(textureRef, 80);
  };

  const handleTextureSelect = (type) => {
    setTextureType(type);
    scrollToRef(sealerRef, 80);
  };

  const handleSealerSelect = (include) => {
    setIncludeSealer(include);
    scrollToRef(resultsRef, 60); // Less offset so user sees results immediately
  };

  // Completion status
  const isSubstrateComplete = !!selectedSubstrate;
  const isSizeComplete = !!selectedSize && area > 0;
  const isColorComplete = !!selectedColor;
  const isTextureComplete = textureType !== null; // Must be explicitly selected (smooth or textured)
  const isSealerComplete = includeSealer !== null; // Must be explicitly selected (true or false)
  const isAllComplete = isSubstrateComplete && isSizeComplete && isColorComplete && isTextureComplete && isSealerComplete;

  return (
    <div className="min-h-screen flex flex-col bg-background texture-noise">
      <Header onColorFanClick={handleColorFanClick} />
      
      <main className="flex-1 pt-16">
        {/* Hero Section */}
        <HeroSection />

        {/* Calculator Title Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="py-6 md:py-8 text-center bg-secondary/30"
        >
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-xl sm:text-2xl font-display text-foreground mb-2">
              {t('heroTitle')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('heroDescription')}
            </p>
          </div>
        </motion.div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

        {/* Step 1: Surface Selection */}
        <SectionWrapper
          stepNumber={1}
          title={t('surfaceTitle')}
          subtitle={t('surfaceSubtitle')}
          description={t('surfaceDescription')}
          isActive={true}
          isCompleted={isSubstrateComplete}
        >
          <SubstrateSelector
            selectedSubstrate={selectedSubstrate}
            onSelect={handleSubstrateSelect}
          />
          
          {/* Warning for risky substrates */}
          <AnimatePresence>
            {hasWarnings && substrateDetails?.warningHe && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Card className="warning-panel mt-6">
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-warning/10 flex items-center justify-center flex-shrink-0">
                          <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-warning" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground mb-1 text-sm sm:text-base">{t('warningTitle')}</p>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {isRTL ? substrateDetails.warningHe : substrateDetails.warningEn}
                          </p>
                        </div>
                      </div>
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="relative w-full sm:w-auto flex-shrink-0"
                      >
                        <motion.div
                          className="absolute -inset-0.5 rounded-lg bg-warning/20"
                          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0, 0.3] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                        <Button 
                          variant="outline" 
                          className="border-warning/50 text-warning hover:bg-warning/10 relative w-full sm:w-auto"
                          asChild
                        >
                          <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer">
                            <MessageCircle className="w-4 h-4 me-2" />
                            {t('contactUs')}
                          </a>
                        </Button>
                      </motion.div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </SectionWrapper>

        {/* Step 2: Size Selection */}
        <AnimatePresence>
          {isSubstrateComplete && (
            <motion.div 
              ref={sizeRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <SectionWrapper
                stepNumber={2}
                title={t('sizeTitle')}
                subtitle={t('sizeSubtitle')}
                description={t('sizeDescription')}
                isActive={isSubstrateComplete}
                isCompleted={isSizeComplete}
              >
                <SizeSelector
                  selectedSize={selectedSize}
                  onSelect={handleSizeSelect}
                  customArea={customArea}
                  onCustomAreaChange={setCustomArea}
                />
              </SectionWrapper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 3: Color Selection */}
        <AnimatePresence>
          {isSizeComplete && (
            <motion.div 
              ref={colorRef}
              id="colors-section"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <SectionWrapper
                stepNumber={3}
                title={t('colorTitle')}
                subtitle={t('colorSubtitle')}
                description={t('colorDescription')}
                isActive={isSizeComplete}
                isCompleted={isColorComplete}
              >
                <ColorSelector
                  selectedColor={selectedColor}
                  onSelect={handleColorSelect}
                />
              </SectionWrapper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 4: Texture Selection */}
        <AnimatePresence>
          {isColorComplete && (
            <motion.div 
              ref={textureRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <SectionWrapper
                stepNumber={4}
                title={t('textureTitle')}
                subtitle={t('textureSubtitle')}
                description={t('textureDescription')}
                isActive={isColorComplete}
                isCompleted={isTextureComplete}
              >
                <TextureSelector
                  textureType={textureType}
                  onSelect={handleTextureSelect}
                />
              </SectionWrapper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 5: Sealer Selection */}
        <AnimatePresence>
          {isTextureComplete && (
            <motion.div 
              ref={sealerRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <SectionWrapper
                stepNumber={5}
                title={t('sealerTitle')}
                subtitle={t('sealerSubtitle')}
                description={t('sealerDescription')}
                isActive={isTextureComplete}
                isCompleted={isSealerComplete}
              >
                <SealerSelector
                  includeSealer={includeSealer}
                  onSelect={handleSealerSelect}
                />
              </SectionWrapper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 6: Calculator Results & Summary */}
        <AnimatePresence>
          {isAllComplete && calculations && (
            <motion.div 
              ref={resultsRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <SectionWrapper
                stepNumber={6}
                title={t('calculatorTitle')}
                subtitle={isRTL ? 'Material Calculator' : 'מחשבון חומרים'}
                isActive={true}
                isCompleted={true}
              >
                <CalculatorResults
                  calculations={calculations}
                  selectedPrimer={selectedPrimer}
                />
              </SectionWrapper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 7: Summary (Moved before Application Guide) */}
        <AnimatePresence>
          {isAllComplete && calculations && (
            <motion.div 
              ref={summaryRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <SectionWrapper
                stepNumber={7}
                title={t('summaryTitle')}
                subtitle={t('summarySubtitle')}
                isActive={true}
                isCompleted={true}
              >
                <Summary
                  substrateDetails={substrateDetails}
                  selectedSize={selectedSize}
                  area={area}
                  selectedColor={selectedColor}
                  textureType={textureType}
                  includeSealer={includeSealer}
                  selectedPrimer={selectedPrimer}
                  calculations={calculations}
                  generateSummary={generateSummary}
                  onStartOver={clearSelections}
                />
              </SectionWrapper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 8: Application Guide (Collapsible) */}
        <AnimatePresence>
          {isAllComplete && calculations && (
            <motion.div 
              ref={guideRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <SectionWrapper
                stepNumber={8}
                title={t('guideTitle')}
                subtitle={t('guideSubtitle')}
                description={t('guideDescription')}
                isActive={true}
                isCompleted={true}
              >
                <ApplicationGuide 
                  includeSealer={includeSealer} 
                  substrateDetails={substrateDetails}
                  textureType={textureType}
                />
              </SectionWrapper>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Golden Oz — Featured Artwork Preview (links to /golden-oz dedicated page) */}
      <GoldenOzPreview />

      {/* Gallery Section - At the end of page */}
      <ProductGallery />

      {/* Video Showcase - Between Gallery and Sample Pack */}
      <VideoShowcase />

      {/* System Article - SEO Content */}
      <SystemArticle />

      {/* Sample Pack CTA - Below Article */}
      <SamplePackCTA />

      <Footer />
      <Toaster position={isRTL ? 'top-left' : 'top-right'} richColors />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <Routes>
          <Route path="/" element={<MicrolitGuide />} />
          <Route path="/golden-oz" element={<GoldenOzRoute />} />
          <Route path="*" element={<MicrolitGuide />} />
        </Routes>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
